import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import useNRLazyQuery from '../../../../graphql/useNRLazyQuery';
import { GET_VULNERABILITY_JIRA_TICKETS } from '../../../../graphql/queries/jiraIntegration';
import JiraIcon from '../../../../NRIcons/IntegrationIcons/JiraIcon';
import { FiltersContext } from '../../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import { RBAC_PERMISSIONS } from '../../../../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { ACR_RESOURCE_TYPE } from '../../../../pages/NRSettings/NRUsersAndAccess/rbac.constants';
import { CONSTANTS } from '../../../../constants/constants';
import WithPermissions from '../../../Permissions/WithPermissions';
import { CREATE_JIRA_TICKET_LABELS } from './createJiraTicket.constants';
import NRCreateVulnerabilityJiraTicket from './NRCreateVulnerabilityJiraTicket';
import NRJiraTicketsTable from './NRJiraTicketsTable';
import {
  getTicketCountLabel,
  VULNERABILITY_JIRA_TICKETS_LABELS,
  VULNERABILITY_JIRA_TICKETS_POLL_INTERVAL_MS
} from './vulnerabilityJiraTickets.constants';

function NRActiveVulnerabilityJiraTickets({ advisoryId, assetId, vulnerabilityData, cveDetails, sx }) {
  const theme = useTheme();
  const { drawerContent, setDrawerContent, drawerTitle, setDrawerTitle, setHasBack, setOpenDrawer } = useContext(FiltersContext);

  const componentId = vulnerabilityData.componentId;

  const ticketArgs = useMemo(
    () => ({
      assetId,
      advisoryId,
      componentId
    }),
    [advisoryId, assetId, componentId]
  );

  const [tickets, ticketsLoading, , fetchTickets, , , startPolling, stopPolling] = useNRLazyQuery({
    query: GET_VULNERABILITY_JIRA_TICKETS,
    qlObjKey: 'vulnerabilityJiraTickets',
    defaultValue: []
  });

  useEffect(() => {
    fetchTickets({
      variables: { args: ticketArgs }
    });
  }, [fetchTickets, ticketArgs]);

  useEffect(() => {
    if (!tickets.length) {
      stopPolling();
      return undefined;
    }

    startPolling(VULNERABILITY_JIRA_TICKETS_POLL_INTERVAL_MS);
    return stopPolling;
  }, [startPolling, stopPolling, tickets.length]);

  const handleCreateTicket = useCallback(
    event => {
      event?.stopPropagation();

      if (drawerContent.length) {
        setHasBack(true);
      }

      setDrawerTitle([...drawerTitle, CREATE_JIRA_TICKET_LABELS.DRAWER_TITLE]);
      setDrawerContent([
        ...drawerContent,
        <NRCreateVulnerabilityJiraTicket
          key="create-jira-ticket"
          vulnerabilityData={vulnerabilityData}
          cveDetails={cveDetails}
          assetId={assetId}
          advisoryId={advisoryId}
        />
      ]);
      setOpenDrawer(true);
    },
    [advisoryId, assetId, cveDetails, drawerContent, drawerTitle, setDrawerContent, setDrawerTitle, setHasBack, setOpenDrawer, vulnerabilityData]
  );

  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    mt: theme.spacing(2)
  };

  const ticketCountLabel = tickets.length ? getTicketCountLabel(tickets.length) : VULNERABILITY_JIRA_TICKETS_LABELS.NO_TICKETS;

  const sectionHeaderContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Avatar
          sx={{
            width: theme.spacing(6),
            height: theme.spacing(6),
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.iconInfo
          }}
        >
          <JiraIcon width={theme.spacing(4)} height={theme.spacing(4)} />
        </Avatar>
        <Box sx={{ ml: theme.spacing(1), minWidth: 0 }}>
          <Typography variant="label" color="text.secondary">
            {VULNERABILITY_JIRA_TICKETS_LABELS.SECTION_LABEL}
          </Typography>
          <Typography variant="h3" color="text.primary">
            {ticketCountLabel}
          </Typography>
        </Box>
      </Box>
      <WithPermissions
        permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}
        rbacPermission={RBAC_PERMISSIONS.JIRA_TICKETS_CREATE}
        resourceType={ACR_RESOURCE_TYPE.ASSET}
      >
        <Button variant="outlined" color="secondary" onClick={handleCreateTicket} sx={{ flexShrink: 0, ml: theme.spacing(2) }}>
          {VULNERABILITY_JIRA_TICKETS_LABELS.CREATE_TICKET}
        </Button>
      </WithPermissions>
    </Box>
  );

  const sectionHeader = <Box sx={{ p: theme.spacing(2) }}>{sectionHeaderContent}</Box>;

  return (
    <Box sx={[sectionSx, sx]}>
      {ticketsLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: theme.spacing(3)
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : tickets.length ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: theme.spacing(2),
              py: theme.spacing(2),
              '& .MuiAccordionSummary-content': {
                margin: 0,
                width: '100%'
              }
            }}
          >
            {sectionHeaderContent}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <NRJiraTicketsTable tickets={tickets} />
          </AccordionDetails>
        </Accordion>
      ) : (
        sectionHeader
      )}
    </Box>
  );
}

export default NRActiveVulnerabilityJiraTickets;
